import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="export-status"
export default class extends Controller {
  static targets = [
    "statusBadge",
    "progressBar",
    "progressText",
    "itemsContainer",
  ];
  static values = {
    url: String,
    completed: { type: Boolean, default: false },
    refreshInterval: { type: Number, default: 3000 },
  };

  connect() {
    if (!this.completedValue) {
      this.startPolling();
    }
  }

  disconnect() {
    this.stopPolling();
  }

  startPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }

    // Initial fetch
    this.fetchStatus();

    this.pollInterval = setInterval(() => {
      this.fetchStatus();
    }, this.refreshIntervalValue);
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  async fetchStatus() {
    try {
      const response = await fetch(this.urlValue, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.updateStatus(data);
    } catch (error) {
      console.error("Error fetching export status:", error);
    }
  }

  updateStatus(data) {
    // Update overall progress bar if exists
    if (this.hasProgressBarTarget && data.progress !== undefined) {
      this.progressBarTarget.style.width = `${data.progress}%`;
    }

    // Update overall progress text if exists
    if (this.hasProgressTextTarget && data.progress !== undefined) {
      this.progressTextTarget.textContent = `${data.progress}%`;
    }

    // Update per-item progress
    if (data.items && data.items.length > 0) {
      this.updateItemsProgress(data.items);

      // Check if all items are done (completed or failed)
      const allItemsDone = data.items.every(
        (item) => item.status === "completed" || item.status === "failed",
      );
      const hasAnyPendingOrProcessing = data.items.some(
        (item) => item.status === "pending" || item.status === "processing",
      );

      // Reload page when all items are done and there were items processing
      if (allItemsDone && !hasAnyPendingOrProcessing) {
        this.stopPolling();
        setTimeout(() => {
          window.location.reload();
        }, 500);
        return;
      }
    }

    // Check if completed or failed (for exports without items)
    if (
      (data.status === "completed" || data.status === "failed") &&
      (!data.items || data.items.length === 0)
    ) {
      this.stopPolling();
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }

  updateItemsProgress(items) {
    items.forEach((item) => {
      // Update progress bar for this item
      const progressBar = document.querySelector(
        `[data-item-progress-bar="${item.id}"]`,
      );
      if (progressBar) {
        progressBar.style.width = `${item.progress}%`;

        // Update color based on status
        progressBar.classList.remove(
          "bg-gray-400",
          "bg-blue-500",
          "bg-cta",
          "bg-red-500",
        );
        switch (item.status) {
          case "completed":
            progressBar.classList.add("bg-cta");
            break;
          case "failed":
            progressBar.classList.add("bg-red-500");
            break;
          case "processing":
            progressBar.classList.add("bg-blue-500");
            break;
          default:
            progressBar.classList.add("bg-gray-400");
        }
      }

      // Update progress text for this item
      const progressText = document.querySelector(
        `[data-item-progress-text="${item.id}"]`,
      );
      if (progressText) {
        progressText.textContent = `${item.progress}%`;
      }
    });
  }
}
